/**
 * Mirror of /app/backend/currency.py — shared between the React web build
 * and the Expo Web export. Static seed rates as of Feb 2026.
 *
 * NOTE: ARS is highly volatile (Argentine inflation). It is the first
 * candidate for live FX migration.
 */
export const FX_RATES_USD = {
    USD: 1.0,
    EUR: 0.92,
    GBP: 0.79,
    AUD: 1.52,
    INR: 83.2,
    AED: 3.6725,
    SGD: 1.34,
    MYR: 4.55,
    THB: 35.0,
    IDR: 15800,
    JPY: 151.5,
    KRW: 1340,
    RUB: 92.0,
    BRL: 5.1,
    ARS: 1010,
};

export const CURRENCY_SYMBOL = {
    USD: "$",
    EUR: "\u20AC",
    GBP: "\u00A3",
    AUD: "A$",
    INR: "\u20B9",
    AED: "\u062F.\u0625",
    SGD: "S$",
    MYR: "RM",
    THB: "\u0E3F",
    IDR: "Rp",
    JPY: "\u00A5",
    KRW: "\u20A9",
    RUB: "\u20BD",
    BRL: "R$",
    ARS: "AR$",
};

export const ZERO_DECIMAL = new Set(["JPY", "KRW", "IDR"]);

// Display order requested for the dropdown — grouped roughly by region.
export const CURRENCY_OPTIONS = [
    { code: "USD", name: "US Dollar", country: "United States" },
    { code: "EUR", name: "Euro", country: "Eurozone" },
    { code: "GBP", name: "British Pound", country: "United Kingdom" },
    { code: "AUD", name: "Australian Dollar", country: "Australia" },
    { code: "INR", name: "Indian Rupee", country: "India" },
    { code: "AED", name: "UAE Dirham", country: "United Arab Emirates" },
    { code: "SGD", name: "Singapore Dollar", country: "Singapore" },
    { code: "MYR", name: "Malaysian Ringgit", country: "Malaysia" },
    { code: "THB", name: "Thai Baht", country: "Thailand" },
    { code: "IDR", name: "Indonesian Rupiah", country: "Indonesia" },
    { code: "JPY", name: "Japanese Yen", country: "Japan" },
    { code: "KRW", name: "South Korean Won", country: "South Korea" },
    { code: "RUB", name: "Russian Ruble", country: "Russia" },
    { code: "BRL", name: "Brazilian Real", country: "Brazil" },
    { code: "ARS", name: "Argentine Peso", country: "Argentina" },
].map((c) => ({
    ...c,
    label: `${c.code} ${CURRENCY_SYMBOL[c.code]}`,
}));

export function isSupported(code) {
    return Boolean(code) && Object.prototype.hasOwnProperty.call(FX_RATES_USD, code);
}

export function convertAmount(amount, from, to) {
    const F = (from || "USD").toUpperCase();
    const T = (to || "USD").toUpperCase();
    if (!isSupported(F) || !isSupported(T)) {
        return { amount, currency: F, rate: 1 };
    }
    if (F === T) return { amount, currency: T, rate: 1 };
    const usd = amount / FX_RATES_USD[F];
    let converted = usd * FX_RATES_USD[T];
    converted = ZERO_DECIMAL.has(T)
        ? Math.round(converted)
        : Math.round(converted * 100) / 100;
    return { amount: converted, currency: T, rate: FX_RATES_USD[T] / FX_RATES_USD[F] };
}

/**
 * Format a price using Intl when available, otherwise fall back to a manual
 * "{symbol}{amount}" string with the right number of decimals. Some browsers
 * format ARS / RUB awkwardly so we keep the manual fallback for them.
 *
 * Phase 14.8.3 — input-shape hardening. Accepts:
 *   * `null` / `undefined`             → returns `null` (React-safe child).
 *   * primitive `number`               → formatted as INR (default backend ccy).
 *   * primitive numeric `string`       → coerced via Number(), formatted as INR.
 *   * `{amount, currency}` object      → formatted via Intl with that currency.
 *   * already-formatted `string`       → passed through unchanged.
 *
 * Rationale: backend price emissions vary by agent — `hotel_agent` ships
 * `{amount, currency}`, `multi_modal_agent` ships primitives + separate
 * `currency`, some legacy paths ship pre-formatted strings. Routing all
 * card renders through this single helper prevents the React #31 crash
 * we saw in incognito (production 2026-06-19) when an object slipped
 * into a JSX text slot inside `ItineraryCard.HotelRow`.
 */
export function formatPrice(price) {
    // null-ish → render nothing.
    if (price == null) return null;
    // Already a string → pass through (assume caller pre-formatted).
    if (typeof price === "string") return price || null;
    // Primitive number → format as INR.
    if (typeof price === "number" && Number.isFinite(price)) {
        return _format({ amount: price, currency: "INR" });
    }
    // Object — must carry an `amount` field. If `amount` itself isn't
    // numeric, give up safely (never throw, never return the object).
    if (typeof price === "object" && price.amount !== undefined) {
        const n = Number(price.amount);
        if (!Number.isFinite(n)) return null;
        const ccy = (price.currency || "INR").toString().toUpperCase();
        return _format({ amount: n, currency: ccy });
    }
    return null;
}

function _format({ amount, currency }) {
    const decimals = ZERO_DECIMAL.has(currency) ? 0 : 2;
    if (typeof Intl !== "undefined" && Intl.NumberFormat) {
        try {
            if (!["ARS", "RUB"].includes(currency)) {
                return new Intl.NumberFormat(undefined, {
                    style: "currency",
                    currency,
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                }).format(amount);
            }
        } catch {
            /* fall through */
        }
    }
    const sym = CURRENCY_SYMBOL[currency] || currency + " ";
    const n = Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    return `${sym}${n}`;
}

export function resolveDisplay(price, displayPrice, currencyPref) {
    if (!price) return { display: null, original: null, sameCurrency: true };
    const target = (currencyPref || price.currency || "USD").toUpperCase();
    let display;
    if (displayPrice && displayPrice.currency === target) {
        display = { amount: displayPrice.amount, currency: target };
    } else {
        const c = convertAmount(price.amount, price.currency, target);
        display = { amount: c.amount, currency: c.currency };
    }
    return {
        display,
        original: price,
        sameCurrency: display.currency === price.currency,
    };
}

/**
 * Format a duration expressed in minutes into a compact "{h}h {m}m"
 * string. Returns `null` (React-safe) for any non-finite / missing
 * input — prevents the production "NaNm" render we hit on 2026-06-19
 * for short-haul fallback flight cards that lack `duration_minutes`.
 *
 * Examples:
 *   formatDuration(75)        → "1h 15m"
 *   formatDuration(135)       → "2h 15m"
 *   formatDuration(60)        → "1h"
 *   formatDuration(45)        → "45m"
 *   formatDuration(undefined) → null
 *   formatDuration(NaN)       → null
 *   formatDuration("210")     → "3h 30m"  (coerced)
 */
export function formatDuration(mins) {
    if (mins == null) return null;
    if (typeof mins === "string" && mins.trim() === "") return null;
    const n = Number(mins);
    if (!Number.isFinite(n) || n < 0) return null;
    const h = Math.floor(n / 60);
    const m = Math.round(n % 60);
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
}

/**
 * Format a stop count + optional first-layover code into a label.
 * Returns `null` for any non-finite input — prevents the "undefined
 * stops" render. The renderer should `&&`-guard on the return value.
 *
 * Examples:
 *   formatStops(0)             → "Non-stop"
 *   formatStops(1, "DXB")      → "1 stop · DXB"
 *   formatStops(2)             → "2 stops"
 *   formatStops(undefined)     → null
 *   formatStops(null)          → null
 */
export function formatStops(count, firstLayoverCode) {
    if (count == null) return null;
    const n = Number(count);
    if (!Number.isFinite(n) || n < 0) return null;
    if (n === 0) return "Non-stop";
    const base = `${n} stop${n === 1 ? "" : "s"}`;
    return firstLayoverCode ? `${base} · ${firstLayoverCode}` : base;
}
